import { useCallback, useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import type { AppShell } from "@/App"
import { getAppConfig, invokeAction, patchAppConfig, type AppConfigField } from "@/lib/api"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { ArrowLeft, Save, Copy, BookOpen, CheckCircle2, Circle, Send } from "lucide-react"

interface SettingsProps {
  shell: AppShell | null
}

type FieldGroup = {
  title: string
  description?: string
  keys: string[]
}

const GROUPS: FieldGroup[] = [
  {
    title: "Shopify",
    description: "Credenciais dos webhooks do Shopify (paga · editada · cancelada). Sem isto, encomendas reais não entram.",
    keys: ["shopify_shop_domain", "shopify_webhook_secret"],
  },
  {
    title: "Armazém e logística",
    description: "Destinos de email e parâmetros de expedição Correos Expresso.",
    keys: ["warehouse_email", "company_display_name", "warehouse_operators", "default_label_type", "default_package_weight_kg"],
  },
  {
    title: "ETICDATA",
    description: "Como criar documentos no ETICDATA via ZLINK Hub.",
    keys: [
      "eticdata_country_id",
      "eticdata_doc_type_customer",
      "eticdata_doc_type_supplier",
      "eticdata_supplier_seccao",
      "fallback_nif",
    ],
  },
  {
    title: "Criar artigos (defaults ETICDATA)",
    description: "Valores usados ao criar artigos novos no ETICDATA.",
    keys: [
      "eticdata_create_family",
      "eticdata_create_category",
      "eticdata_create_type",
      "eticdata_create_unit",
      "eticdata_create_tax_sale",
      "eticdata_create_tax_purchase",
      "zlink_stock_warehouse",
    ],
  },
  {
    title: "Fornecedores",
    description: "Mapeamento marca → supplier_code do ETICDATA. Determina o fornecedor de cada produto pela marca ao criar encomendas a fornecedor.",
    keys: ["supplier_brand_map"],
  },
  {
    title: "Fontes das encomendas",
    description:
      "Secção do ETICDATA → fonte (ONLINE, BALCÃO, COMERCIAL, EXPORTAÇÃO) e email que recebe o aviso de encomenda separada dessa fonte.",
    keys: ["order_sources"],
  },
  {
    title: "Lembretes de pagamento",
    description:
      "Encomendas por pagar (transferência, Multibanco) iniciam um fluxo do Koortex que envia os lembretes ao cliente — esperas, textos e nº de lembretes editam-se no fluxo e nos templates de email. Requer o webhook \"Order creation\". No fluxo: entradas encomenda, shopify_order_id, cliente, email, valor, metodo, dados_pagamento, data, empresa (também disponíveis nos templates como {{encomenda}}, {{valor}}…); antes de cada lembrete, \"Executar ação de app\" → app:shopify-fulfillment:order_payment_status com {\"shopify_order_id\": \"{{trigger.shopify_order_id}}\"} e uma condição em stop.",
    keys: ["payment_reminder_flow_id", "payment_reminder_methods", "bank_transfer_details"],
  },
  {
    title: "Encomendas do ETICDATA (balcão, comercial, exportação)",
    description:
      "Encomendas criadas diretamente no ETICDATA entram na app como pagas, na validação de stock, e seguem para a separação. Sem data de arranque não entra nada. A sincronização agenda-se num fluxo do Koortex (Executar ação de app → app:shopify-fulfillment:sync_eticdata_orders): de 10 em 10 min com days=2 e 1× por dia com days=30. A fatura destas encomendas é registada à mão.",
    keys: ["eticdata_sync_since", "eticdata_sync_sections", "eticdata_sync_document_types", "free_shipping_threshold", "eticdata_sync_max_per_run"],
  },
  {
    title: "Aviso de separação concluída",
    description: "Quando o armazém marca a encomenda como COMPLETA, estes emails recebem o aviso (e notificação no Koortex, se forem utilizadores).",
    keys: ["billing_notify_emails", "google_chat_webhook_url"],
  },
  {
    title: "Portes a orçamentar (ilhas)",
    description: "Encomendas pagas sem portes (ex.: 'Envio Personalizado', ilhas) ficam em 'Aguarda portes' — não seguem para a ENC nem para o armazém até registarem os portes pagos.",
    keys: ["quote_shipping_keywords", "quote_shipping_islands"],
  },
  {
    title: "Levantamento em loja",
    description: "Email automático ao cliente quando a encomenda de levantamento é separada. Placeholders: {cliente} {encomenda}.",
    keys: ["pickup_ready_email_subject", "pickup_ready_email_body", "pickup_info_block"],
  },
  {
    title: "Relatório financeiro",
    description:
      "Excel com encomendas, pagamentos, reembolsos, comissões e faturas ETICDATA, enviado por email. O envio agenda-se num fluxo do Koortex (Executar ação de app → app:shopify-fulfillment:daily_report) — diário ou semanal (com o período em daily_report_days).",
    keys: ["daily_report_emails", "daily_report_days", "report_split_by_method", "payment_fee_rules"],
  },
  {
    title: "Automação",
    description: "Cron de re-validação e gates automáticos.",
    keys: ["auto_create_enc", "stock_recheck_cron", "auto_send_warehouse_pdf", "auto_issue_invoice"],
  },
  {
    title: "Criação de artigos",
    description: "Sufixo de marca no nome ao criar artigos no ETICDATA (ex: SYLVANIA → SYL).",
    keys: ["brand_name_suffix_map"],
  },
  {
    title: "Email de atraso",
    description: "Texto do email enviado ao cliente quando a encomenda atrasa. Placeholders: {cliente} {encomenda} {previsao} {nota}.",
    keys: ["delay_email_subject", "delay_email_body"],
  },
  {
    title: "Recolha Correos",
    description: "Janela horária e antecedência da recolha na morada do remetente.",
    keys: ["pickup_time_from", "pickup_time_to", "pickup_lead_days"],
  },
  {
    title: "Impressão de etiquetas",
    description: "Servidor de impressão (atrás de Cloudflare Access).",
    keys: [
      "print_server_url",
      "print_auth_token",
      "print_cf_access_client_id",
      "print_cf_access_client_secret",
    ],
  },
]

const SECRET_KEYS = new Set([
  "shopify_webhook_secret",
  "print_auth_token",
  "print_cf_access_client_secret",
])

// Campos de string que devem renderizar como textarea (multi-linha, largura total).
const MULTILINE_KEYS = new Set([
  "delay_email_body",
  "warehouse_operators",
  "daily_report_emails",
  "billing_notify_emails",
  "pickup_ready_email_body",
  "pickup_info_block",
  "quote_shipping_keywords",
])

export function Settings({ shell }: SettingsProps) {
  const navigate = useNavigate()
  const [schema, setSchema] = useState<Record<string, AppConfigField>>({})
  const [values, setValues] = useState<Record<string, unknown>>({})
  const [readonly, setReadonly] = useState(false)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Raw text + parse error for object (JSON) fields, keyed by field name.
  const [jsonText, setJsonText] = useState<Record<string, string>>({})
  const [jsonError, setJsonError] = useState<Record<string, string | null>>({})
  const [sendingReport, setSendingReport] = useState(false)

  const load = useCallback(async () => {
    if (!shell) return
    try {
      const res = await getAppConfig(shell)
      setSchema(res.schema)
      setValues(res.config)
      setReadonly(res.readonly)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setLoading(false)
    }
  }, [shell])

  useEffect(() => {
    void load()
  }, [load])

  const groupedKeys = useMemo(() => {
    const known = new Set(GROUPS.flatMap((g) => g.keys))
    const extra = Object.keys(schema).filter((k) => !known.has(k))
    if (extra.length === 0) return GROUPS
    return [...GROUPS, { title: "Outros", keys: extra }]
  }, [schema])

  async function onSave() {
    if (!shell) return
    const badJson = Object.entries(jsonError).find(([, v]) => v)
    if (badJson) {
      shell.bridge.toast({ variant: "error", description: `${badJson[0]}: JSON inválido` })
      return
    }
    setSaving(true)
    try {
      const patch: Record<string, unknown> = {}
      for (const key of Object.keys(schema)) {
        if (values[key] !== undefined) patch[key] = values[key]
      }
      const next = await patchAppConfig(shell, patch)
      setValues(next)
      setJsonText({})
      shell.bridge.toast({ variant: "success", description: "Settings guardadas." })
    } catch (err) {
      shell.bridge.toast({
        variant: "error",
        description: err instanceof Error ? err.message : String(err),
      })
    } finally {
      setSaving(false)
    }
  }

  // Corre o relatório diário já (mesma ação que o fluxo agendado). Usa as
  // settings GUARDADAS — gravar antes se mudaste os emails.
  async function onSendReport() {
    if (!shell) return
    setSendingReport(true)
    try {
      const res = await invokeAction<{
        orders: number
        advanced: number
        hub_error: string | null
        sent_to: string[]
      }>(shell, "daily_report", {})
      if (!res.success) throw new Error(res.error ?? "Falhou")
      const d = res.data
      shell.bridge.toast({
        variant: d?.hub_error ? "error" : "success",
        description: d
          ? `Relatório enviado a ${d.sent_to.join(", ")} · ${d.orders} encomendas` +
            (d.advanced ? ` · ${d.advanced} avançada(s) para expedição` : "") +
            (d.hub_error ? ` · Hub sem resposta: ${d.hub_error}` : "")
          : "Relatório enviado.",
      })
    } catch (err) {
      shell.bridge.toast({ variant: "error", description: err instanceof Error ? err.message : String(err) })
    } finally {
      setSendingReport(false)
    }
  }

  if (!shell) {
    return <div className="p-6 text-sm text-muted-foreground">Standalone dev — sem dados.</div>
  }

  // Settings a partir de GESTOR (5). Abaixo disso a app é só logística.
  if (shell.role < 5) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <Card className="max-w-md border-amber-300 bg-amber-50/40">
          <CardHeader>
            <CardTitle>Acesso restrito</CardTitle>
            <CardDescription>
              As settings desta app só estão disponíveis a gestores e
              administradores do tenant.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button variant="outline" onClick={() => navigate("/")}>
              <ArrowLeft className="mr-1.5 size-3.5" />
              Voltar
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  const hostOrigin = shell.context.hostOrigin ?? (typeof window !== "undefined" ? window.location.origin : "")
  const tenantSlug = shell.context.tenantId
  const webhookUrlFor = (subpath: string) =>
    `${hostOrigin}/api/apps/shopify-fulfillment/webhook/${subpath}?tenant=${tenantSlug}`
  // Os webhooks a criar no Shopify Admin (mesmo secret em todos).
  const webhooks = [
    { subpath: "orders-paid", event: "Order payment", what: "encomenda paga → entra na app" },
    { subpath: "orders-updated", event: "Order update", what: "edições, pagamentos e reembolsos" },
    { subpath: "orders-cancelled", event: "Order cancellation", what: "cancelamento" },
    { subpath: "orders-created", event: "Order creation", what: "por pagar → lembretes de pagamento (opcional)" },
  ].map((w) => ({ ...w, url: webhookUrlFor(w.subpath) }))

  const hasSecret = Boolean(
    values.shopify_webhook_secret && String(values.shopify_webhook_secret).trim(),
  )
  const hasWarehouseEmail = Boolean(values.warehouse_email && String(values.warehouse_email).trim())
  const hasShopDomain = Boolean(values.shopify_shop_domain && String(values.shopify_shop_domain).trim())

  function copyWebhook(url: string) {
    if (!shell) return
    void navigator.clipboard.writeText(url)
    shell.bridge.toast({ variant: "success", description: "URL copiada." })
  }

  return (
    <div className="flex h-full flex-col">
      <header className="flex items-center justify-between border-b px-6 py-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate("/")}>
            <ArrowLeft className="size-4" />
          </Button>
          <div>
            <h1 className="text-lg font-semibold leading-tight">Settings</h1>
            <p className="text-xs text-muted-foreground">
              Configuração da app — guardada em <code>bundle_app_installations.config_json</code>.
            </p>
          </div>
        </div>
        <Button onClick={onSave} disabled={saving || readonly || loading}>
          <Save className="mr-1.5 size-3.5" />
          {saving ? "A guardar…" : "Guardar"}
        </Button>
      </header>

      <div className="flex-1 overflow-auto p-6 space-y-4">
        {error && (
          <Card className="border-destructive/30 bg-destructive/5">
            <CardContent className="py-3 text-sm text-destructive">{error}</CardContent>
          </Card>
        )}

        {readonly && (
          <Card className="border-amber-300 bg-amber-50/40">
            <CardContent className="py-3 text-sm">
              Esta é uma instalação fixture local (dev). A config não persiste — instala a app a
              partir do catálogo do admin para editar settings reais.
            </CardContent>
          </Card>
        )}

        {loading && <Card><CardContent className="py-6 text-sm text-muted-foreground">A carregar…</CardContent></Card>}

        {!loading && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2">
                <CheckCircle2 className="size-4 text-muted-foreground" />
                Pré-requisitos
              </CardTitle>
              <CardDescription>
                Para receber encomendas reais do Shopify, três coisas têm de estar prontas.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3 pt-2 text-sm">
              <ol className="space-y-3">
                <li className="flex items-start gap-3">
                  {hasShopDomain ? (
                    <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-emerald-600" />
                  ) : (
                    <Circle className="mt-0.5 size-4 shrink-0 text-amber-500" />
                  )}
                  <div className="min-w-0">
                    <div className="font-medium">Domínio Shopify</div>
                    <div className="text-xs text-muted-foreground">
                      Define <code className="rounded bg-muted px-1">shopify_shop_domain</code> em baixo (ex: <code>minha-loja.myshopify.com</code>).
                    </div>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  {hasSecret ? (
                    <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-emerald-600" />
                  ) : (
                    <Circle className="mt-0.5 size-4 shrink-0 text-amber-500" />
                  )}
                  <div className="min-w-0">
                    <div className="font-medium">Webhook secret guardado</div>
                    <div className="text-xs text-muted-foreground">
                      Cola o shared secret do Shopify em <code className="rounded bg-muted px-1">shopify_webhook_secret</code>. Sem isto o webhook responde 401.
                    </div>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  {hasWarehouseEmail ? (
                    <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-emerald-600" />
                  ) : (
                    <Circle className="mt-0.5 size-4 shrink-0 text-amber-500" />
                  )}
                  <div className="min-w-0">
                    <div className="font-medium">Email do armazém</div>
                    <div className="text-xs text-muted-foreground">
                      Necessário para enviar o PDF de separação após criação da ENC.
                    </div>
                  </div>
                </li>
              </ol>

              <div className="rounded-md border bg-muted/30 p-3 space-y-2">
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground">URLs dos webhooks (cola no Shopify)</div>
                {webhooks.map((w) => (
                  <div key={w.subpath} className="space-y-1">
                    <div className="text-xs">
                      <strong>{w.event}</strong> <span className="text-muted-foreground">— {w.what}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 break-all rounded bg-background px-2 py-1 font-mono text-xs">{w.url}</code>
                      <Button size="sm" variant="outline" onClick={() => copyWebhook(w.url)}>
                        <Copy className="mr-1.5 size-3.5" />
                        Copiar
                      </Button>
                    </div>
                  </div>
                ))}
                <div className="text-xs text-muted-foreground">
                  Shopify Admin → Settings → Notifications → Webhooks → um webhook por evento acima · JSON · API 2024-10.
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {!loading && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2">
                <BookOpen className="size-4 text-muted-foreground" />
                Documentação
              </CardTitle>
              <CardDescription>Como funciona o fluxo end-to-end e onde ir quando algo falha.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3 pt-2 text-sm">
              <div className="rounded-md border bg-muted/20 p-4 space-y-3">
                <div>
                  <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Fluxo</div>
                  <p className="mt-1 text-sm">
                    Webhook Shopify → validação de stock no ETICDATA → encomenda a fornecedor (se rotura) → criação da ENC cliente → PDF de separação → picking → fatura → expedição Correos Expresso.
                  </p>
                </div>
                <div>
                  <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Gates humanos</div>
                  <ul className="mt-1 list-disc space-y-1 pl-5 text-sm">
                    <li>Aprovação da encomenda a fornecedor (quando há rotura).</li>
                    <li>Confirmar separação no armazém (após receber PDF).</li>
                    <li>Confirmar fatura emitida no ETICDATA (FA-SW sem NIF, FAT-W com NIF).</li>
                    <li>Criar expedição Correos Expresso quando o picking termina.</li>
                  </ul>
                </div>
                <div>
                  <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Falha no ETICDATA</div>
                  <p className="mt-1 text-sm">
                    Quando o ZLINK Hub rejeita o payload (ex: NIF inválido, doc_type errado), a encomenda fica em <code className="rounded bg-muted px-1">eticdata=error</code>. Corrige as settings em baixo e clica <em>Tentar novamente</em> no detalhe da encomenda.
                  </p>
                </div>
                <div>
                  <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Mais</div>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Guia completo de deploy: <code className="rounded bg-muted px-1">docs-dev/reference/shopify-fulfillment-deploy.md</code> no repo do host.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {!loading && groupedKeys.map((group) => (
          <Card key={group.title}>
            <CardHeader className="pb-2">
              <CardTitle>{group.title}</CardTitle>
              {group.description && <CardDescription>{group.description}</CardDescription>}
            </CardHeader>
            <CardContent className="grid grid-cols-1 gap-x-6 gap-y-4 pt-2 sm:grid-cols-2">
              {group.keys.filter((key) => schema[key]).map((key) => {
                const field = schema[key]
                const current = values[key] ?? field.default ?? ""
                const labelHint = field.required ? "obrigatório" : "opcional"
                const isObject = field.type === "object"
                const isMultiline = MULTILINE_KEYS.has(key)
                return (
                  <label
                    key={key}
                    className={
                      "flex flex-col gap-1 text-sm" + (isObject || isMultiline ? " sm:col-span-2" : "")
                    }
                  >
                    <span className="flex items-center justify-between">
                      <span className="font-medium">{key}</span>
                      <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{labelHint}</span>
                    </span>
                    {isObject ? (
                      <>
                        <textarea
                          className="min-h-28 rounded-md border bg-background px-2 py-1.5 font-mono text-xs"
                          spellCheck={false}
                          value={
                            jsonText[key] ??
                            JSON.stringify(values[key] ?? field.default ?? {}, null, 2)
                          }
                          onChange={(e) => {
                            const raw = e.target.value
                            setJsonText((t) => ({ ...t, [key]: raw }))
                            try {
                              const parsed = raw.trim() === "" ? {} : JSON.parse(raw)
                              setValues((v) => ({ ...v, [key]: parsed }))
                              setJsonError((er) => ({ ...er, [key]: null }))
                            } catch {
                              setJsonError((er) => ({ ...er, [key]: "JSON inválido" }))
                            }
                          }}
                          disabled={readonly}
                        />
                        {jsonError[key] && (
                          <span className="text-xs text-destructive">{jsonError[key]}</span>
                        )}
                      </>
                    ) : isMultiline ? (
                      <textarea
                        className="min-h-40 rounded-md border bg-background px-3 py-2 text-sm leading-relaxed"
                        value={current === null ? "" : String(current)}
                        placeholder={field.default !== undefined ? String(field.default) : ""}
                        onChange={(e) => setValues((v) => ({ ...v, [key]: e.target.value }))}
                        disabled={readonly}
                      />
                    ) : field.type === "boolean" ? (
                      <select
                        className="h-9 rounded-md border bg-background px-2 text-sm"
                        value={current ? "true" : "false"}
                        onChange={(e) => setValues((v) => ({ ...v, [key]: e.target.value === "true" }))}
                        disabled={readonly}
                      >
                        <option value="false">Desligado</option>
                        <option value="true">Ligado</option>
                      </select>
                    ) : (
                      <Input
                        type={
                          SECRET_KEYS.has(key)
                            ? "password"
                            : field.type === "number"
                              ? "number"
                              : "text"
                        }
                        autoComplete={SECRET_KEYS.has(key) ? "new-password" : undefined}
                        value={current === null ? "" : String(current)}
                        placeholder={field.default !== undefined ? String(field.default) : ""}
                        onChange={(e) => {
                          const raw = e.target.value
                          setValues((v) => ({
                            ...v,
                            [key]: field.type === "number" ? (raw === "" ? null : Number(raw)) : raw,
                          }))
                        }}
                        disabled={readonly}
                      />
                    )}
                    {field.description && (
                      <span className="text-xs text-muted-foreground">{field.description}</span>
                    )}
                  </label>
                )
              })}
              {group.title === "Relatório financeiro" && (
                <div className="flex items-center gap-3 sm:col-span-2">
                  <Button variant="outline" size="sm" disabled={sendingReport} onClick={() => void onSendReport()}>
                    <Send className="mr-1.5 size-3.5" />
                    {sendingReport ? "A enviar…" : "Enviar relatório agora"}
                  </Button>
                  <span className="text-xs text-muted-foreground">Usa as settings guardadas.</span>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  )
}
